import { useEffect, useRef } from 'react'
import type { iframeResizer } from '@iframe-resizer/child'
import { isFramed } from '../../../utils/isFramed'

export const FRAMED_VIEWPORT_HEIGHT_PROPERTY = '--framed-parent-viewport-height'

const VISIBLE_TOP_MARGIN = 24

/**
 * Sizes and positions the self-schedule dialog against the parent page's real viewport
 * when running inside a partner iframe, instead of the iframe's own box.
 */
export const useFramedModalViewport = (open: boolean, popup: HTMLDivElement | null): void => {
  const hasScrolledRef = useRef(false)

  useEffect(() => {
    if (!open || !popup || !isFramed()) return undefined

    const parentIFrame = window.parentIFrame
    if (!parentIFrame) return undefined

    hasScrolledRef.current = false

    const onParentProps = (props: iframeResizer.ParentProps) => {
      // direction:'none' holds the frame at its ~632px slot, so a taller parent viewport would let
      // the dialog overflow it and clip the confirm button; cap the published height to the frame.
      const cappedHeight = Math.min(props.viewport.height, window.innerHeight)
      popup.style.setProperty(FRAMED_VIEWPORT_HEIGHT_PROPERTY, `${cappedHeight}px`)

      // getParentProps recalls this on every parent scroll or resize; scroll only once.
      if (!hasScrolledRef.current) {
        hasScrolledRef.current = true
        const { top } = popup.getBoundingClientRect()
        parentIFrame.scrollToOffset(0, Math.max(0, top - VISIBLE_TOP_MARGIN))
      }
    }

    // Despite its doc comment, this returns an unsubscribe function.
    const unsubscribe = parentIFrame.getParentProps(onParentProps)

    return () => {
      unsubscribe()
      popup.style.removeProperty(FRAMED_VIEWPORT_HEIGHT_PROPERTY)
    }
  }, [open, popup])
}
